import { React, useEffect, useMemo, useState } from "react";
import PageTitle from "../../PageTitle";
import useAxios from "../../../utilities/useAxios";
import { getCurrentUserProfile } from "../../../utilities/AuthService";
import {
	Modal,
	Form,
	Input,
	Button,
	message,
	ConfigProvider,
	Typography,
} from "antd";
import { CalendarOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import locale from "antd/lib/locale/ko_KR";
import {
	generateTimeSlots,
	formatSlotLabel,
	slotKey,
	parseSlotKey,
	slotToDayjs,
	isDateDisabledForNonOffice,
	isDateRentableForNonOffice,
	isSlotGloballyAvailable,
	getAvailableRooms,
	areSlotsContiguous,
	getRoomDisplayLabel,
} from "./rentSanctuaryUtils";

dayjs.locale("ko");

const { TextArea } = Input;
const { Text } = Typography;
const BookRentalSanctuary = () => {
	const [form] = Form.useForm();
	const [sanctuaryRoomList, setSanctuaryRoomList] = useState([]);
	const [bookings, setBookings] = useState([]);
	const [selectedDate, setSelectedDate] = useState(null);
	const [calendarMonth, setCalendarMonth] = useState(dayjs());
	const [selectedSlotKeys, setSelectedSlotKeys] = useState([]);
	const [modalVisible, setModalVisible] = useState(false);
	const [selectedRoom, setSelectedRoom] = useState(null);
	const [isOtherSanctuary, setIsOtherSanctuary] = useState(false);
	const [loading, setLoading] = useState(false);

	const timeSlots = useMemo(() => generateTimeSlots(), []);

	useEffect(() => {
		loadSanctuaryRooms();
		loadBookings();
	}, []);

	const loadSanctuaryRooms = async () => {
		try {
			const response = await useAxios.get("/utils/sanctuary/list/");
			if (response.status === 200) {
				const list = [...response.data].sort((a, b) => {
					if (a.sanctuary_name === b.sanctuary_name) {
						return a.room_name.localeCompare(b.room_name);
					}
					return a.sanctuary_name.localeCompare(b.sanctuary_name);
				});
				setSanctuaryRoomList(list);
			}
		} catch {
			message.error("성전 목록을 불러오는데 실패했습니다.");
		}
	};

	const loadBookings = async () => {
		try {
			const [scheduleRes, applyRes] = await Promise.all([
				useAxios.get("/utils/rental/sanctuary/schedule/"),
				useAxios.get("/utils/rental/sanctuary/apply/"),
			]);
			const approved = scheduleRes.status === 200 ? scheduleRes.data : [];
			const pending =
				applyRes.status === 200
					? applyRes.data.filter((a) => a.status === "신청")
					: [];
			setBookings([...approved, ...pending]);
		} catch {
			message.error("일정을 불러오는데 실패했습니다.");
		}
	};

	const dayBookings = useMemo(() => {
		if (!selectedDate) return [];
		return bookings.filter((b) =>
			dayjs(b.start_datetime).isSame(selectedDate, "day"),
		);
	}, [bookings, selectedDate]);

	const selectedSlots = useMemo(
		() => selectedSlotKeys.map(parseSlotKey),
		[selectedSlotKeys],
	);

	const availableRooms = useMemo(() => {
		if (!selectedDate || !selectedSlots.length) return [];
		return getAvailableRooms(
			sanctuaryRoomList,
			selectedSlots,
			selectedDate,
			dayBookings,
		);
	}, [sanctuaryRoomList, selectedSlots, selectedDate, dayBookings]);

	const handleDateSelect = (date) => {
		if (isDateDisabledForNonOffice(date)) return;
		setSelectedDate(date);
		setSelectedSlotKeys([]);
		setSelectedRoom(null);
		setIsOtherSanctuary(false);
	};

	const handleSlotClick = (slot) => {
		if (!selectedDate) return;
		const key = slotKey(slot);
		const available = isSlotGloballyAvailable(
			slot,
			selectedDate,
			sanctuaryRoomList,
			dayBookings,
		);
		if (!available) return;

		if (selectedSlotKeys.includes(key)) {
			const next = selectedSlotKeys.filter((k) => k !== key);
			setSelectedSlotKeys(next);
			setSelectedRoom(null);
			setIsOtherSanctuary(false);
			return;
		}

		const next = [...selectedSlotKeys, key];
		if (!areSlotsContiguous(next)) {
			message.warning("연속된 시간만 선택할 수 있습니다.");
			return;
		}
		setSelectedSlotKeys(next);
		setSelectedRoom(null);
		setIsOtherSanctuary(false);
	};

	const openApplyModal = async (room, isOther = false) => {
		setSelectedRoom(room);
		setIsOtherSanctuary(isOther);
		form.resetFields();
		try {
			const profile = await getCurrentUserProfile();
			if (profile) {
				form.setFieldsValue({
					representative: profile.name || "",
					representative_tel: profile.phone_number || "",
				});
			}
		} catch {
			// ignore
		}
		setModalVisible(true);
	};

	const handleSubmit = async (values) => {
		if (!selectedDate || !selectedSlots.length) return;

		setLoading(true);
		try {
			const sortedKeys = sortSlotKeys(selectedSlotKeys);
			const firstSlot = parseSlotKey(sortedKeys[0]);
			const lastSlot = parseSlotKey(sortedKeys[sortedKeys.length - 1]);

			const startDateTime = slotToDayjs(selectedDate, firstSlot);
			const endDateTime = slotToDayjs(selectedDate, lastSlot).add(
				30,
				"minute",
			);

			let sanctuary_room = isOtherSanctuary ? null : selectedRoom?.id;
			let custom_sanctuary_name = "";
			if (isOtherSanctuary) {
				custom_sanctuary_name = (values.custom_sanctuary_name || "").trim();
			}

			const formData = {
				sanctuary_room,
				custom_sanctuary_name,
				start_datetime: startDateTime.format("YYYY-MM-DD HH:mm:ss"),
				end_datetime: endDateTime.format("YYYY-MM-DD HH:mm:ss"),
				rent_team: values.rent_team,
				rent_for: values.rent_for,
				representative: values.representative,
				representative_tel: values.representative_tel,
			};

			const response = await useAxios.post(
				"/utils/rental/sanctuary/apply/",
				formData,
			);

			if (response.status === 201) {
				message.success("성전 대여 신청이 완료되었습니다.");
				setModalVisible(false);
				setSelectedSlotKeys([]);
				setSelectedRoom(null);
				setIsOtherSanctuary(false);
				form.resetFields();
				loadBookings();
			}
		} catch (error) {
			message.error(
				error.response?.data?.error ||
					"신청 처리 중 오류가 발생했습니다.",
			);
		} finally {
			setLoading(false);
		}
	};

	const buildCalendarDays = () => {
		const start = calendarMonth.startOf("month").startOf("week");
		const end = calendarMonth.endOf("month").endOf("week");
		const days = [];
		let current = start;
		while (current.isBefore(end) || current.isSame(end, "day")) {
			days.push(current);
			current = current.add(1, "day");
		}
		return days;
	};

	const calendarDays = buildCalendarDays();
	const amSlots = timeSlots.filter((s) => s.hour < 12);
	const pmSlots = timeSlots.filter((s) => s.hour >= 12);

	const renderTimeButton = (slot) => {
		const key = slotKey(slot);
		const isSelected = selectedSlotKeys.includes(key);
		const disabled =
			!selectedDate ||
			!isSlotGloballyAvailable(
				slot,
				selectedDate,
				sanctuaryRoomList,
				dayBookings,
			);

		return (
			<button
				key={key}
				type="button"
				className={`utils-rent_sanctuary__book-time-btn${
					isSelected
						? " utils-rent_sanctuary__book-time-btn--selected"
						: ""
				}${disabled ? " utils-rent_sanctuary__book-time-btn--disabled" : ""}`}
				disabled={disabled}
				onClick={() => handleSlotClick(slot)}>
				{formatSlotLabel(slot)}
			</button>
		);
	};

	const sortSlotKeys = (keys) =>
		[...keys].sort((a, b) => {
			const sa = parseSlotKey(a);
			const sb = parseSlotKey(b);
			return sa.hour * 60 + sa.minute - (sb.hour * 60 + sb.minute);
		});

	const modalTitle = useMemo(() => {
		if (!selectedDate || !selectedSlots.length) return "성전 대여 신청";
		const sortedKeys = sortSlotKeys(selectedSlotKeys);
		const first = parseSlotKey(sortedKeys[0]);
		const last = parseSlotKey(sortedKeys[sortedKeys.length - 1]);
		const start = slotToDayjs(selectedDate, first);
		const end = slotToDayjs(selectedDate, last).add(30, "minute");
		const roomLabel = isOtherSanctuary
			? "기타"
			: selectedRoom
				? getRoomDisplayLabel(selectedRoom)
				: "";
		return `${selectedDate.format("YYYY.MM.DD")} ${start.format("HH:mm")}~${end.format("HH:mm")} ${roomLabel}`;
	}, [
		selectedDate,
		selectedSlots,
		selectedSlotKeys,
		selectedRoom,
		isOtherSanctuary,
	]);

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="성전 대여 신청" />
			<div className="utils-rent_sanctuary__book">
				<div className="utils-rent_sanctuary__book-header">
					<CalendarOutlined />
					<span>날짜와 시간을 선택해 주세요</span>
				</div>

				<div className="utils-rent_sanctuary__book-picker">
				<div className="utils-rent_sanctuary__book-calendar">
					<div className="utils-rent_sanctuary__book-calendar__nav">
						<button
							type="button"
							className="utils-rent_sanctuary__book-nav-btn"
							onClick={() =>
								setCalendarMonth(
									calendarMonth.subtract(1, "month").clone(),
								)
							}>
							<LeftOutlined />
						</button>
						<span className="utils-rent_sanctuary__book-calendar__title">
							{calendarMonth.format("YYYY.M")}
						</span>
						<button
							type="button"
							className="utils-rent_sanctuary__book-nav-btn"
							onClick={() =>
								setCalendarMonth(
									calendarMonth.add(1, "month").clone(),
								)
							}>
							<RightOutlined />
						</button>
					</div>
					<div className="utils-rent_sanctuary__book-calendar__weekdays">
						{["일", "월", "화", "수", "목", "금", "토"].map((d) => (
							<span key={d}>{d}</span>
						))}
					</div>
					<div className="utils-rent_sanctuary__book-calendar__grid">
						{calendarDays.map((date) => {
							const isCurrentMonth = date.isSame(
								calendarMonth,
								"month",
							);
							const isToday = date.isSame(dayjs(), "day");
							const isSelected =
								selectedDate &&
								date.isSame(selectedDate, "day");
							const disabled = isDateDisabledForNonOffice(date);

							return (
								<button
									key={date.format("YYYY-MM-DD")}
									type="button"
									className={`utils-rent_sanctuary__book-day${
										!isCurrentMonth
											? " utils-rent_sanctuary__book-day--outside"
											: ""
									}${isSelected ? " utils-rent_sanctuary__book-day--selected" : ""}${
										disabled
											? " utils-rent_sanctuary__book-day--disabled"
											: ""
									}`}
									disabled={disabled}
									onClick={() => handleDateSelect(date)}>
									<span className="utils-rent_sanctuary__book-day__num">
										{date.date()}
									</span>
									{isToday && (
										<span className="utils-rent_sanctuary__book-day__today">
											오늘
										</span>
									)}
								</button>
							);
						})}
					</div>
				</div>

				{selectedDate && (
					<div className="utils-rent_sanctuary__book-times">
						<p className="utils-rent_sanctuary__book-times__hint">
							대여 시간은 선택하신 시간 + 30분으로 설정됩니다
						</p>
						<div className="utils-rent_sanctuary__book-times__section">
							<span className="utils-rent_sanctuary__book-times__label">
								오전
							</span>
							<div className="utils-rent_sanctuary__book-times__grid">
								{amSlots.map(renderTimeButton)}
							</div>
						</div>
						<div className="utils-rent_sanctuary__book-times__section">
							<span className="utils-rent_sanctuary__book-times__label">
								오후
							</span>
							<div className="utils-rent_sanctuary__book-times__grid">
								{pmSlots.map(renderTimeButton)}
							</div>
						</div>
					</div>
				)}

				{selectedDate &&
					!isDateRentableForNonOffice(selectedDate) && (
					<p className="utils-rent_sanctuary__book-notice">
						대여 가능한 날짜가 아닙니다.
					</p>
				)}
				</div>

				{selectedDate && selectedSlotKeys.length > 0 && (
					<div className="utils-rent_sanctuary__book-rooms">
						<h3 className="utils-rent_sanctuary__book-rooms__title">
							대여 가능한 성전
						</h3>
						{availableRooms.length === 0 && (
							<p className="utils-rent_sanctuary__book-rooms__empty">
								선택한 시간에 대여 가능한 성전이 없습니다.
							</p>
						)}
						<div className="utils-rent_sanctuary__book-rooms__list">
							{availableRooms.map((room) => (
								<button
									key={room.id}
									type="button"
									className="utils-rent_sanctuary__book-room-card"
									onClick={() => openApplyModal(room)}>
									{getRoomDisplayLabel(room)}
								</button>
							))}
							<button
								type="button"
								className="utils-rent_sanctuary__book-room-card utils-rent_sanctuary__book-room-card--other"
								onClick={() => openApplyModal(null, true)}>
								기타
							</button>
						</div>
					</div>
				)}

			</div>

			<Modal
				title={modalTitle}
				open={modalVisible}
				onCancel={() => {
					setModalVisible(false);
					form.resetFields();
				}}
				footer={null}
				destroyOnClose
				className="utils-rent_sanctuary__book-modal">
				<Form form={form} layout="vertical" onFinish={handleSubmit}>
					{isOtherSanctuary && (
						<Form.Item
							name="custom_sanctuary_name"
							label="성전 이름"
							rules={[
								{
									required: true,
									message: "성전 이름을 입력해주세요",
								},
							]}>
							<Input placeholder="신청할 성전 이름을 입력하세요" />
						</Form.Item>
					)}
					<Form.Item
						name="rent_team"
						label="교구/기관명"
						rules={[
							{
								required: true,
								message: "교구/기관명을 입력해주세요",
							},
						]}>
						<Input placeholder="교구/기관명을 입력하세요" />
					</Form.Item>
					<Form.Item
						name="rent_for"
						label="사용 목적"
						rules={[
							{
								required: true,
								message: "사용 목적을 입력해주세요",
							},
						]}>
						<TextArea rows={4} placeholder="사용 목적을 입력하세요" />
					</Form.Item>
					<Form.Item
						name="representative"
						label="신청자 이름"
						rules={[
							{
								required: true,
								message: "신청자 이름을 입력해주세요",
							},
						]}>
						<Input placeholder="신청자 이름을 입력하세요" />
					</Form.Item>
					<Form.Item
						name="representative_tel"
						label="신청자 연락처"
						rules={[
							{
								required: true,
								message: "신청자 연락처를 입력해주세요",
							},
							{
								pattern: /^[0-9-]+$/,
								message: "숫자와 하이픈(-)만 입력 가능합니다",
							},
						]}>
						<Input placeholder="연락처를 입력하세요 (예: 010-1234-5678)" />
					</Form.Item>
					<Form.Item>
						<Text type="danger">
							아브라함성전 전체가 예약되어 있으면 개별 강의실 대여 및
							신청이 불가능합니다.
							<br />
							또한 아브라함성전 강의실이 예약되어 있으면 성전 전체
							대여 및 신청이 불가능합니다.
						</Text>
					</Form.Item>
					<Form.Item>
						<Button
							type="primary"
							htmlType="submit"
							loading={loading}
							block>
							신청하기
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</ConfigProvider>
	);
};

export default BookRentalSanctuary;
